// src/services/axiosInstance.ts

import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosRequestConfig,
} from "axios";
import toast from "react-hot-toast";
import { authService } from "./authService";
import { apiBaseUrl } from "../config/environment";

interface FailedQueueItem {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

// Standard API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  code?: number;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface PaginatedApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T[];
  meta: {
    page: number;
    limit: number;
    totalPages: number;
    totalDocs: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  };
  code?: number;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Needed for cookie-based refresh token handling
});

let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  failedQueue = [];
};

let definitiveFailureScheduled = false;

function handleDefinitiveAuthFailure() {
  if (definitiveFailureScheduled) return;
  definitiveFailureScheduled = true;

  try {
    const here = window.location.pathname + window.location.search;
    if (here && !here.startsWith('/signin')) {
      sessionStorage.setItem('returnTo', here);
    }
  } catch {
    /* non-fatal */
  }

  const SECONDS = 5;
  let remaining = SECONDS;
  const toastId = toast.error(
    `Your session has expired. Redirecting to sign-in in ${remaining}s...`,
    { duration: SECONDS * 1000 + 500 }
  );

  const tick = setInterval(() => {
    remaining -= 1;
    if (remaining > 0) {
      toast.error(
        `Your session has expired. Redirecting to sign-in in ${remaining}s...`,
        { id: toastId, duration: remaining * 1000 + 500 }
      );
    }
  }, 1000);

  setTimeout(() => {
    clearInterval(tick);
    authService.logout();
    window.location.href = '/signin';
  }, SECONDS * 1000);
}

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = authService.getToken();

    if (token && config.headers) {
      (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${token}`;
    }

    // Add active department header for department-aware APIs
    const activeDepartment = authService.getActiveDepartment();
    if (activeDepartment && config.headers) {
      (config.headers as AxiosRequestHeaders)['X-Active-Department'] = activeDepartment;
    }

    // Default instance Content-Type is application/json. For multipart uploads, the browser must set
    // multipart/form-data with a boundary; otherwise Express parses the body as JSON and multer sees no file.
    if (config.data instanceof FormData) {
      const headers = AxiosHeaders.from(config.headers ?? {});
      headers.delete('Content-Type');
      config.headers = headers;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor to handle new API format and refresh token on 401 error
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Skip formatting for blob/arraybuffer responses
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response;
    }

    // Handle successful responses with new format
    if (response.data && typeof response.data === 'object') {
      // If it's our standard API format, pass it through
      if ('success' in response.data) {
        return response;
      }

      // Handle backend format with 'status' property
      if ('status' in response.data && response.data.status === 'success') {
        // Convert to our standard format
        const standardResponse: ApiResponse = {
          success: true,
          message: response.data.message || 'Operation completed successfully',
          data: response.data.data
        };
        response.data = standardResponse;
        return response;
      }

      // For legacy responses, wrap them in standard format
      const standardResponse: ApiResponse = {
        success: true,
        message: 'Operation completed successfully',
        data: response.data
      };
      response.data = standardResponse;
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle standardized error responses
    if (error.response?.data && typeof error.response.data === 'object') {
      const errorData = error.response.data as any;

      // If it's already in our standard format, extract the message
      if ('success' in errorData && !errorData.success) {
        const standardError = new Error(errorData.message || 'An error occurred');
        (standardError as any).response = error.response;
        (standardError as any).code = errorData.code || error.response.status;
        (standardError as any).errors = errorData.errors;

        // Don't retry for client errors (400-499) except 401
        if (error.response.status !== 401 && error.response.status >= 400 && error.response.status < 500) {
          return Promise.reject(standardError);
        }
      }
    }

    // Handle 401 errors with token refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !(originalRequest.url || "").includes("/auth/refresh") &&
      !(originalRequest.url || "").includes("/auth/login")
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const success = await authService.refreshToken();

        if (success) {
          const newAccessToken = authService.getToken();

          if (newAccessToken) {
            api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
            processQueue(null, newAccessToken);

            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            }

            return api(originalRequest);
          }
          processQueue(new Error('No access token after refresh'), null);
          handleDefinitiveAuthFailure();
          return Promise.reject(new Error('No access token after refresh'));
        }

        processQueue(new Error('Token refresh failed'), null);
        handleDefinitiveAuthFailure();
        return Promise.reject(new Error('Token refresh failed'));
      } catch (refreshError: any) {
        processQueue(refreshError, null);

        // Only log out if refresh endpoint explicitly rejected us.
        // 5xx and network errors keep the session intact.
        const status = refreshError?.response?.status;
        if (status === 401 || status === 400) {
          handleDefinitiveAuthFailure();
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Create standardized error for non-API errors (ensure message is always a string)
    const rawMessage = (error.response?.data as any)?.message ??
      error.message ??
      'An unexpected error occurred';
    const message = typeof rawMessage === 'string' ? rawMessage : String(rawMessage ?? 'An unexpected error occurred');

    const standardError = new Error(message);
    (standardError as any).response = error.response;
    (standardError as any).code = error.response?.status ?? 500;

    return Promise.reject(standardError);
  }
);

export default api;

// Test-only: reset the definitive-failure guard so each test can exercise
// the toast/countdown flow in isolation. Not for production use.
export const __resetAuthFailureGuardForTests = () => {
  definitiveFailureScheduled = false;
};
